import React, { useEffect, useMemo, useState } from 'react';
import { ToastList } from '@devesharp/react/web';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useParams } from 'react-router-dom';
import { Observable } from 'rxjs';
import {
   CompanyZoopGetResolve,
   EstablishmentZoopCreateResolve,
   EstablishmentZoopDocumentUploadResolve,
   MMCSearchResolve,
} from '~/api';
import { accountType, bankCodes } from '~/services/data';
import history from '~/config/history';

const REQUIRED_DOC_TYPES = ['SELFIE', 'RG_FRONT', 'RG_BACK'] as const;

const DOC_LABELS: Record<string, string> = {
   SELFIE: 'Selfie do sócio',
   RG_FRONT: 'RG do sócio (frente)',
   RG_BACK: 'RG do sócio (verso)',
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ALLOWED_MIME_PREFIXES = ['image/', 'application/pdf'];

const yupValidateZoopCreateSchema = Yup.object().shape({
   mcc: Yup.string().required('Este campo é obrigatório'),
   statement_descriptor: Yup.string().required('Este campo é obrigatório'),
   bank_type: Yup.string().nullable().required('Este campo é obrigatório'),
   bank_code: Yup.number().nullable().required('Este campo é obrigatório'),
   bank_routing_number: Yup.number().typeError('Este campo é obrigatório').required('Este campo é obrigatório'),
   bank_account_number: Yup.number().typeError('Este campo é obrigatório').required('Este campo é obrigatório'),
   user_login: Yup.string().required('Este campo é obrigatório'),
   user_password: Yup.string().required('Este campo é obrigatório'),
});

function hasPendingUploads(pendingFiles: Record<string, File | null>): boolean {
   return REQUIRED_DOC_TYPES.some((type) => Boolean(pendingFiles[type]));
}

function toPromise<T>(observable: Observable<T>): Promise<T> {
   return new Promise((resolve, reject) => {
      observable.subscribe({
         next: resolve,
         error: reject,
      });
   });
}

function isAllowedFile(file: File): boolean {
   return ALLOWED_MIME_PREFIXES.some((prefix) => file.type.startsWith(prefix));
}

export function useEstablishmentZoopPage(formRef: React.RefObject<FormHandles>): any {
   const { companyId } = useParams<any>();
   const [company, setCompany] = useState<any>(null);
   const [MCCs, setMCCs] = useState<any[]>([]);
   const [loadingCompany, setLoadingCompany] = useState(true);
   const [saving, setSaving] = useState(false);
   const [pendingFiles, setPendingFiles] = useState<Record<string, File | null>>({});
   const [documentErrors, setDocumentErrors] = useState<Record<string, string>>({});

   const hasZoopSeller = Boolean(company?.has_zoop_seller);

   const existingDocuments = useMemo(() => {
      const map: Record<string, any> = {};
      (company?.documents ?? []).forEach((doc: any) => {
         map[doc.type] = doc;
      });
      return map;
   }, [company?.documents]);

   document.title = hasZoopSeller
      ? 'Editar Zoop - XPAG Tecnologia'
      : 'Cadastro Zoop - XPAG Tecnologia';

   useEffect(() => {
      let pending = 2;

      function finishLoading() {
         pending -= 1;
         if (pending <= 0) {
            setLoadingCompany(false);
         }
      }

      CompanyZoopGetResolve(companyId).subscribe(
         (companyData) => {
            setCompany(companyData);

            if (formRef.current) {
               formRef.current.setData({
                  bank_type: 'checking',
                  ...companyData,
               });
            }

            finishLoading();
         },
         () => {
            ToastList.show({
               message: 'Não foi possível carregar o estabelecimento',
               type: 'error',
            });
            history.push('/establishment');
            finishLoading();
         },
      );

      MMCSearchResolve({}).subscribe(
         (mccData) => {
            setMCCs(Array.isArray(mccData) ? mccData : mccData?.results ?? []);
            finishLoading();
         },
         () => finishLoading(),
      );
   }, [companyId, formRef]);

   function onDocumentChange(type: string, file?: File | null) {
      const errors = { ...documentErrors };
      delete errors[type];

      if (!file) {
         setPendingFiles((current) => ({ ...current, [type]: null }));
         setDocumentErrors(errors);
         return;
      }

      if (file.size > MAX_FILE_SIZE) {
         errors[type] = 'Arquivo deve ter no máximo 5MB';
         setDocumentErrors(errors);
         setPendingFiles((current) => ({ ...current, [type]: null }));
         return;
      }

      if (!isAllowedFile(file)) {
         errors[type] = 'Formato inválido. Use PNG, JPEG, PDF ou similar';
         setDocumentErrors(errors);
         setPendingFiles((current) => ({ ...current, [type]: null }));
         return;
      }

      setPendingFiles((current) => ({ ...current, [type]: file }));
      setDocumentErrors(errors);
   }

   function validateDocuments(requireMissing: boolean): Record<string, string> {
      const errors: Record<string, string> = {};

      REQUIRED_DOC_TYPES.forEach((type) => {
         const hasExisting = Boolean(existingDocuments[type]);
         const hasPending = Boolean(pendingFiles[type]);

         if (requireMissing && !hasExisting && !hasPending) {
            errors[type] = 'Documento obrigatório';
         }
      });

      return errors;
   }

   async function uploadPendingDocuments(): Promise<void> {
      for (const type of REQUIRED_DOC_TYPES) {
         const file = pendingFiles[type];
         if (!file) {
            continue;
         }

         await toPromise(EstablishmentZoopDocumentUploadResolve(companyId, type, file));
      }
   }

   async function onSubmit(data: any) {
      if (hasZoopSeller) {
         if (!hasPendingUploads(pendingFiles)) {
            ToastList.show({
               message: 'Selecione ao menos um documento para enviar',
               type: 'error',
            });
            return;
         }

         setSaving(true);

         try {
            await uploadPendingDocuments();
            setSaving(false);
            ToastList.show({
               message: 'Documentos enviados com sucesso',
               type: 'success',
            });
            history.push(`/establishment/detail/${companyId}`);
         } catch (error: any) {
            setSaving(false);
            ToastList.show({
               message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao enviar documentos',
               type: 'error',
            });
         }

         return;
      }

      try {
         await yupValidateZoopCreateSchema.validate(data, { abortEarly: false });
      } catch (validationError: any) {
         validationError.inner?.forEach((err: any) => {
            formRef.current?.setFieldError(err.path, err.message);
         });
         return;
      }

      const docErrors = validateDocuments(true);
      if (Object.keys(docErrors).length) {
         setDocumentErrors(docErrors);
         ToastList.show({
            message: 'Envie a selfie e o RG (frente e verso) do sócio',
            type: 'error',
         });
         return;
      }

      setSaving(true);

      try {
         await toPromise(EstablishmentZoopCreateResolve(companyId, data));
         await uploadPendingDocuments();

         setSaving(false);
         ToastList.show({
            message: 'Seller criado e documentos enviados com sucesso',
            type: 'success',
         });
         history.push('/establishment');
      } catch (error: any) {
         setSaving(false);
         ToastList.show({
            message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao criar seller na Zoop',
            type: 'error',
         });
      }
   }

   const formInfoData = {
      MCC: [...MCCs]
         .map((i) => ({ value: i.id, name: i.name }))
         .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
      bankCodes: bankCodes.map((i) => ({ value: i.id, name: `${i.id} - ${i.name}` })),
      accountType,
   };

   const documentFields = REQUIRED_DOC_TYPES.map((type) => ({
      type,
      label: DOC_LABELS[type],
      existing: existingDocuments[type],
      pending: pendingFiles[type],
      error: documentErrors[type],
   }));

   return {
      onSubmit,
      onDocumentChange,
      companyId,
      company,
      loadingCompany,
      saving,
      formInfoData,
      hasZoopSeller,
      documentFields,
   };
}
